/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, Fragment, useCallback, useEffect } from "react"
import { Drawer } from "antd"
import { Form } from "grab-ui"
import { useTranslationsMap } from "common/utils/i18n"
import { MenuGroupLinkStoreDrawer } from "components/drawers/MenuGroupLinkStoreDrawer"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { waitMenuGroupPropagete } from "utils/menu_group"
import { AnyAction } from "redux"
import { ResponseWithError } from "types/api"
import notification from "components/common/notification"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import useFetchAllMenuGroupLinkedStoreList from "hooks/useFetchAllMenuGroupLinkedStoreList"
import useFetchAllStoreList from "hooks/useFetchAllStoreList"
import { useHistory } from "react-router"
import { MENUS } from "constants/routes"
import { ActionList } from "./ActionList"
import {
  menuGroupLinkedStoreDrawerItemAction,
  menuGroupLinkedStoreDrawerListAction,
  menuGroupLinkedStoreDrawerStyle,
} from "./styles"
import { drawerTitleStyle } from "../styles"

export interface MenuGroupLinkedStoreDrawerProps extends FormComponentProps {
  visible: boolean
  onClose: () => void
}

export const MenuGroupLinkedStoreDrawerComponent: FC<MenuGroupLinkedStoreDrawerProps> = ({
  visible,
  onClose,
}: MenuGroupLinkedStoreDrawerProps) => {
  const {
    menu: {
      drawer,
      menuGroupDetail: { menuGroupID, latestPropagateStatus },
      merchantGroupID,
      menuGroupLinkedStoresIDs = [],
      outlets,
      stopAfterFirstPage,
      outletsHasMore,
    },
  } = useSelector((state: AppState) => state)

  const { showMenuGroupLinkStore } = drawer

  const linkedStores = menuGroupLinkedStoresIDs
    .map(storeID => {
      const outlet = outlets?.find(o => o.merchantID === storeID)
      return outlet
        ? { label: outlet.merchantName, value: outlet.merchantID }
        : null
    })
    .filter(Boolean) as { label: string; value: string }[]

  const dispatch = useDispatch()
  const history = useHistory()
  const fetchAllMenuGroupLinkedStoreList = useFetchAllMenuGroupLinkedStoreList()

  const textMap = useTranslationsMap({
    title: "menu.menuGroup.linkedStore.drawer.title",
    unlink: "menu.menuGroup.linkedStore.drawer.unlink",
    addStore: "menu.menuGroup.linkedStore.drawer.addStore",
    menuGroupNameLabel: "menu.menuGroup.drawer.labels.name",
    save: "common.save",
    success: "common.success",
    unlinkErrorMessageMFC: "menu.menuGroup.message.unlinkMFC",
  })

  // only show unlink button for non propagating menu group
  // and for normal menu (menuGroupID != storeID)
  const showUnlinkButton =
    latestPropagateStatus === 1 && (menuGroupID ?? "").includes("MENU")

  useEffect(() => {
    if (visible) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupEditStores,
        action: EventAction.pageView,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
        },
      })
    }
  }, [visible])

  const unlinkErrorCallback = useCallback((response: ResponseWithError) => {
    const { message = "" } = response ?? {}
    if (message.includes("MFC")) {
      return textMap.unlinkErrorMessageMFC
    }
    return true
  }, [])

  const handleClickItemAction = useCallback(
    async (storeID: string) => {
      if (!storeID) {
        return
      }
      const t0 = performance.now()
      dispatch(actionCreator.global.showLoading())
      const resp: any = await dispatch(
        actionCreator.menu.unlinkStoreFromMenuGroup({
          merchantGroupID,
          menuGroupID,
          storeIDs: [storeID],
          errorCallback: unlinkErrorCallback,
        }) as AnyAction,
      )
      let labels = {
        MERCHANT_GROUP_ID: merchantGroupID,
        MENU_GROUP_ID: menuGroupID,
        STORE_ID_SELECTED: storeID,
        LOADING_STATUS: "SUCCESS",
        LOAD_TIME: 0,
        REASON: "",
        ERROR_MSG: "",
      }
      if (resp?.type?.endsWith("_SUCCESS")) {
        await waitMenuGroupPropagete({ menuGroupID, merchantGroupID, dispatch })
        const updatedMenuGroupID =
          resp?.payload?.data?.menuGroupID || menuGroupID
        if (updatedMenuGroupID !== menuGroupID) {
          history.replace(`${MENUS}/${updatedMenuGroupID}`)
          // refetch menu group
          await dispatch(
            actionCreator.menu.fetchMenuGroupDetail({
              menuGroupID: updatedMenuGroupID,
              merchantGroupID,
            }),
          )
        } else {
          // refetch linked stores
          fetchAllMenuGroupLinkedStoreList()
          notification.success({
            message: textMap.success,
          })
        }
      } else if (resp?.type?.endsWith("_FAIL")) {
        labels = {
          ...labels,
          LOADING_STATUS: "FAILURE",
          ...getResponseErrorEventLabels(resp),
        }
      }
      const t1 = performance.now()
      labels = {
        ...labels,
        LOAD_TIME: t1 - t0,
      }
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupEditStores,
        action: EventAction.clickUnlink,
        labels,
      })
      dispatch(actionCreator.global.hideLoading())
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, menuGroupID, merchantGroupID, unlinkErrorCallback],
  )

  const fetchAllStoreList = useFetchAllStoreList()

  useEffect(() => {
    if (stopAfterFirstPage && outletsHasMore) {
      fetchAllStoreList({
        disableGlobalLoading: true,
      })
    }
  }, [fetchAllStoreList, stopAfterFirstPage, outletsHasMore])

  return (
    <Fragment>
      <Drawer
        title={<span css={drawerTitleStyle}>{textMap.title}</span>}
        placement="right"
        width={450}
        visible={visible}
        onClose={onClose}
        destroyOnClose
        css={menuGroupLinkedStoreDrawerStyle}
      >
        <ActionList
          data-testid="action-list"
          items={linkedStores}
          itemAction={
            showUnlinkButton && (
              <a
                css={menuGroupLinkedStoreDrawerItemAction}
                className="itemActionContainer"
              >
                {textMap.unlink}
              </a>
            )
          }
          listAction={
            <a css={menuGroupLinkedStoreDrawerListAction}>
              {textMap.addStore.toLocaleLowerCase()}
            </a>
          }
          onClickListAction={() => {
            TroyWebTracker.sendEvent({
              category: EventCategory.menuGroupEditStores,
              action: EventAction.clickLinkNewStores,
              labels: {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              },
            })
            dispatch(
              actionCreator.menu.toggleDrawer({
                showMenuGroupLinkStore: true,
              }),
            )
          }}
          onClickItemAction={(storeID: string) =>
            handleClickItemAction(storeID)
          }
        />
      </Drawer>
      <MenuGroupLinkStoreDrawer
        data-testid="link-store-drawer"
        visible={!!showMenuGroupLinkStore}
        onClose={() => {
          dispatch(
            actionCreator.menu.toggleDrawer({
              showMenuGroupLinkStore: false,
            }),
          )
        }}
      />
    </Fragment>
  )
}
export const MenuGroupLinkedStoreDrawer = Form.create<
  MenuGroupLinkedStoreDrawerProps
>({})(MenuGroupLinkedStoreDrawerComponent)
